import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  getAdminFieldModule,
  getAllCrops,
  getAllFertilizers,
  getAllTillageSeedings,
  updateCropsBauData,
  updateFertilizerBauData,
  updatePesticidesBauData,
  updateTillageSeedingBauData,
} from "services";

import { useCancelAsyncOnUnmount, useTranslation } from "utils";

import { Button, Drawer, InformationCard, Message as message } from "components";

import { CropsBaselineYear } from "pages/app/modules/components/Crop/CropsBaselineYear";
import { FertilizerBaselineYear } from "pages/app/modules/components/Fertilizer/FertilizerBaselineYear";
import { PesticidesBaselineYear } from "pages/app/modules/components/Pesticide/PesticidesBaselineYear";
import { TillageSeedingBaselineYear } from "pages/app/modules/components/TillageSeeding/TillageSeedingBaselineYear";

export const FieldModuleBauDataUpdate = ({ fieldModuleId, visible, onClose, years }) => {
  const signal = useCancelAsyncOnUnmount()();
  const { t } = useTranslation(["modules"]);

  const [bauEntries, setBauEntries] = useState({
    crops: {},
    fertilizer: {},
    pesticides: {},
    tillage_seeding: {},
  });

  const [entriesToDelete, setEntriesToDelete] = useState({
    crops: [],
    fertilizer: [],
    pesticides: [],
    tillage_seeding: [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [globalKey, setGlobalKey] = useState(0);
  const [crops, setCrops] = useState({ crops: [] });
  const [fertilizers, setFertilizers] = useState({});
  const [tillageAndSeedings, setTillageAndSeedings] = useState({});
  const [fieldModule, setFieldModule] = useState();

  const forms = useRef({
    [years[0]]: [],
    [years[1]]: [],
    [years[2]]: [],
  });

  const completeTillageSeedingForm = () => {
    if (fieldModule.paidYears.length) {
      message({ content: "you are not allowed to edit verified data", icon: "error" });
      return;
    }

    updateTillageSeedingBauData({
      id: fieldModule.bauEntry ? fieldModule.bauEntry.id : undefined,
      fieldId: fieldModule.field.id,
      farmId: fieldModule.farmId,
      fieldModuleId: fieldModule.id,
      moduleId: fieldModule.moduleId,
      bauEntries: {
        tillage_seeding: bauEntries.tillage_seeding,
      },
      entriesToDelete,
      signal,
    }).then(() => {
      message({ content: t("fieldModulePage.bauDataUpdated"), icon: "tick" });
      onClose();
      setIsSaving(false);
    });
  };

  const completeFertilizerForm = () => {
    if (fieldModule.paidYears.length) {
      message({ content: "you are not allowed to edit verified data", icon: "error" });
      return;
    }

    updateFertilizerBauData({
      id: fieldModule.bauEntry ? fieldModule.bauEntry.id : undefined,
      fieldId: fieldModule.field.id,
      farmId: fieldModule.farmId,
      fieldModuleId: fieldModule.id,
      moduleId: fieldModule.moduleId,
      bauEntries: {
        fertilizer: bauEntries.fertilizer,
      },
      entriesToDelete,
      signal,
    }).then(() => {
      message({ content: t("fieldModulePage.bauDataUpdated"), icon: "tick" });
      onClose();
      setIsSaving(false);
    });
  };

  const completePesticidesForm = () => {
    if (fieldModule.paidYears.length) {
      message({ content: "you are not allowed to edit verified data", icon: "error" });
      return;
    }

    updatePesticidesBauData({
      id: fieldModule.bauEntry ? fieldModule.bauEntry.id : undefined,
      fieldId: fieldModule.field.id,
      farmId: fieldModule.farmId,
      fieldModuleId: fieldModule.id,
      moduleId: fieldModule.moduleId,
      bauEntries: {
        pesticides: bauEntries.pesticides,
      },
      entriesToDelete,
      signal,
    }).then(() => {
      message({ content: t("fieldModulePage.bauDataUpdated"), icon: "tick" });
      onClose();
      setIsSaving(false);
    });
  };

  const completeCropsForm = () => {
    if (fieldModule.paidYears.length) {
      message({ content: "you are not allowed to edit verified data", icon: "error" });
      return;
    }

    updateCropsBauData({
      id: fieldModule.bauEntry ? fieldModule.bauEntry.id : undefined,
      fieldId: fieldModule.field.id,
      farmId: fieldModule.farmId,
      fieldModuleId: fieldModule.id,
      moduleId: fieldModule.moduleId,
      bauEntries: {
        crops: bauEntries.crops,
      },
      entriesToDelete,
      signal,
    }).then(() => {
      message({ content: t("fieldModulePage.bauDataUpdated"), icon: "tick" });
      onClose();
      setIsSaving(false);
    });
  };

  const handleBauSave = async () => {
    try {
      const formsToValidate = Object.values(forms.current)
        .reduce((acc, forms) => [...acc, ...forms], [])
        .filter((form) => form);

      if (formsToValidate.length > 0) {
        await Promise.all(formsToValidate.map((form) => form.validateFields()));
      }
    } catch (e) {
      if (e.errorFields) {
        // Do nothing, form is invalid. antd validation feedback is now shown
        return;
      }
      throw e;
    }
    setIsSaving(true);
    switch (fieldModule.module.slug) {
      case "fertilizer":
        completeFertilizerForm();
        break;

      case "tillage_seeding":
        completeTillageSeedingForm();
        break;

      case "pesticides":
        completePesticidesForm();
        break;

      case "crops":
        completeCropsForm();
        break;

      default:
        break;
    }
  };

  const handleEntryDeletion = (key, id) => {
    setGlobalKey(globalKey + 1);

    if (!id) {
      // doesn't exist in the DB, we don't need to delete it there
      return;
    }

    setEntriesToDelete({
      ...entriesToDelete,
      [key]: [...entriesToDelete[key], id],
    });
  };

  const handleYearUpdate = (key, year, data) => {
    const updatedEntries = {
      ...bauEntries,
      [key]: {
        ...bauEntries[key],
        [year]: data,
      },
    };

    setBauEntries(updatedEntries);
  };

  const loadFertilizers = useCallback(
    () =>
      getAllFertilizers({
        signal,
      })
        .then((res) => {
          setFertilizers(res);
        })
        .finally(() => {
          setLoading(false);
        }),
    [signal]
  );

  const loadCrops = useCallback(
    () =>
      getAllCrops({
        signal,
      })
        .then((res) => {
          setCrops({ crops: [...res] });
        })
        .finally(() => {
          setLoading(false);
        }),
    [signal]
  );

  const loadTillageSeedings = useCallback(
    () =>
      getAllTillageSeedings({
        signal,
      })
        .then((res) => {
          setTillageAndSeedings(res);
        })
        .finally(() => {
          setLoading(false);
        }),
    [signal]
  );

  const loadPractices = () => {
    switch (fieldModule.module.slug) {
      case "pesticides":
        return setLoading(false);
      case "fertilizer":
        return loadFertilizers();
      case "tillage_seeding":
        return loadTillageSeedings();
      case "crops":
        return loadCrops();
      default:
        break;
    }
  };

  useEffect(() => {
    getAdminFieldModule({
      fieldModuleId: fieldModuleId,
      signal,
    }).then((res) => {
      setFieldModule(res);
      setBauEntries({
        crops: res.cropsBauEntries,
        fertilizer: res.fertilizerBauEntries,
        tillage_seeding: res.tillageSeedingBauEntries,
        pesticides: res.pesticidesBauEntries,
      });
    });
  }, [fieldModuleId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!fieldModule) {
      return;
    }
    setLoading(true);
    loadPractices();
  }, [fieldModule]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    !loading &&
    fieldModule && (
      <Drawer
        visible={visible}
        onClose={onClose}
        title="Update field module baseline data"
        footer={
          <Button
            block
            className="margin-bottom-100"
            loading={loading || isSaving}
            type="primary"
            onClick={handleBauSave}
            disabled={fieldModule.paidYears.length}
          >
            Save
          </Button>
        }
      >
        {fieldModule.paidYears.length > 0 && (
          <InformationCard
            iconName="tabler/pencil"
            variant="alert"
            title="you are not allowed to edit verified data"
            text="The submitted data cannot be edited because it has already undergone verification, and the corresponding payout has been processed."
            className={"margin-bottom-200"}
          />
        )}

        <span className="paragraph-m">Update module baseline data here.</span>
        <section className="container modules tiny">
          {fieldModule.module &&
            Object.keys(bauEntries.crops).length > 0 &&
            fieldModule.module.slug === "crops" &&
            years.map((year, i) => (
              <CropsBaselineYear
                key={`${year}-${globalKey}-${i}`}
                allCrops={crops}
                existingCrops={bauEntries.crops[year]}
                fieldId={fieldModule.field.id}
                forms={forms}
                onDeleteCrop={handleEntryDeletion}
                onUpdate={handleYearUpdate}
                year={year}
              />
            ))}
          <div className="baseline-form">
            {fieldModule.module &&
              Object.keys(bauEntries.fertilizer).length > 0 &&
              fieldModule.module.slug === "fertilizer" &&
              years.map((year, i) => (
                <FertilizerBaselineYear
                  key={`${year}-${globalKey}-${i}-${fieldModule.id}`}
                  existingProducts={bauEntries.fertilizer[year]}
                  fertilizers={fertilizers}
                  fieldModule={fieldModule}
                  forms={forms}
                  year={year}
                  onDeleteProduct={handleEntryDeletion}
                  onUpdate={handleYearUpdate}
                />
              ))}
            {fieldModule.module &&
              Object.keys(bauEntries.pesticides).length > 0 &&
              fieldModule.module.slug === "pesticides" &&
              years.map((year, i) => (
                <PesticidesBaselineYear
                  key={`${year}-${globalKey}-${i}-${fieldModule.id}`}
                  existingPesticides={bauEntries.pesticides[year]}
                  fieldModule={fieldModule}
                  forms={forms}
                  year={year}
                  onUpdate={handleYearUpdate}
                />
              ))}
            {fieldModule.module &&
              Object.keys(bauEntries.tillage_seeding).length > 0 &&
              fieldModule.module.slug === "tillage_seeding" &&
              years.map((year, i) => (
                <TillageSeedingBaselineYear
                  dataTestId={`yearly-baseline-tillage-${i}`}
                  key={`${year}-${globalKey}-${i}-${fieldModule.id}`}
                  existingPractices={bauEntries.tillage_seeding[year]}
                  fieldModule={fieldModule}
                  forms={forms}
                  tillageAndSeedings={tillageAndSeedings}
                  year={year}
                  onDeletePractice={handleEntryDeletion}
                  onUpdate={handleYearUpdate}
                />
              ))}
          </div>
        </section>
      </Drawer>
    )
  );
};
